import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLanguage } from '../components/LanguageContext';
import { useAuth } from '../firebase/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Coins, Users, Shield, Search, RefreshCw, Plus, Equal } from 'lucide-react';
import { toast } from 'sonner';

// Thin authenticated fetch wrapper for the SQLite admin API
async function adminApi(getToken, path, opts = {}) {
  const token = await getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(path, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });
  const text = await res.text();
  let data; try { data = text ? JSON.parse(text) : null; } catch { data = { raw: text }; }
  if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
  return data;
}

export default function Admin() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: currentUser, loading: isLoadingMe, getAuthToken } = useAuth();
  const [search, setSearch] = useState('');
  const [creditInputs, setCreditInputs] = useState({});
  const [createName, setCreateName] = useState('');
  const [createEmail, setCreateEmail] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [createRole, setCreateRole] = useState('user');
  const [createCredits, setCreateCredits] = useState('5');

  const createUserMutation = useMutation({
    mutationFn: ({ name, email, password, role, credits }) =>
      adminApi(getAuthToken, '/api/admin/users', {
        method: 'POST',
        body: JSON.stringify({ name, email, password, role, credits }),
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries(['adminUsers']);
      const generated = result?.generatedPassword;
      if (generated) {
        toast.success(
          (language === 'he' ? 'נוצר משתמש. סיסמה: ' : 'User created. Password: ') +
            generated
        );
      } else {
        toast.success(language === 'he' ? 'נוצר משתמש' : 'User created');
      }

      setCreateName('');
      setCreateEmail('');
      setCreatePassword('');
      setCreateRole('user');
      setCreateCredits('5');
    },
    onError: (err) => {
      toast.error(err?.message || (language === 'he' ? 'יצירת משתמש נכשלה' : 'Failed to create user'));
    },
  });

  const handleCreateUser = (e) => {
    e.preventDefault();
    const credits = parseInt(createCredits, 10);
    createUserMutation.mutate({
      name: createName,
      email: createEmail,
      password: createPassword,
      role: createRole,
      credits: Number.isFinite(credits) ? credits : 5,
    });
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString();
  };

  useEffect(() => {
    // Don't redirect while role is still being loaded from the backend
    if (isLoadingMe) return;
    if (!currentUser) { navigate('/login', { replace: true }); return; }
    // Role may not have loaded yet from SQLite — wait briefly
    if (currentUser.role === undefined) return;
    if (currentUser.role !== 'admin') {
      toast.error(language === 'he' ? 'אין לך הרשאת מנהל' : 'You do not have admin permission');
      navigate('/dashboard', { replace: true });
    }
  }, [currentUser, isLoadingMe, navigate, language]);

  const {
    data: usersData,
    isLoading,
    isFetching,
    refetch: refetchUsers,
  } = useQuery({
    queryKey: ['adminUsers'],
    queryFn: () => adminApi(getAuthToken, '/api/admin/users'),
    enabled: !!currentUser && currentUser.role === 'admin',
  });
  const users = usersData?.users || usersData || [];

  const updateUserMutation = useMutation({
    mutationFn: ({ userId, updates }) =>
      adminApi(getAuthToken, `/api/admin/users/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(['adminUsers']);
      toast.success(
        language === 'he' ? 'המשתמש עודכן בהצלחה' : 'User updated successfully'
      );
    },
    onError: (err) => {
      toast.error(
        (language === 'he' ? 'עדכון המשתמש נכשל: ' : 'Failed to update user: ') + (err?.message || '')
      );
    },
  });

  const filteredUsers = useMemo(() => {
    if (!search) return users;
    const term = search.toLowerCase();
    return users.filter((u) => {
      const name = (u.name || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      const role = (u.role || '').toLowerCase();
      return (
        name.includes(term) ||
        email.includes(term) ||
        role.includes(term)
      );
    });
  }, [users, search]);

  const stats = useMemo(() => {
    const totalUsers = users.length;
    const totalCredits = users.reduce(
      (sum, u) => sum + (Number.isFinite(u.credits) ? u.credits : 0),
      0
    );
    const admins = users.filter((u) => u.role === 'admin').length;

    return {
      totalUsers,
      totalCredits,
      admins,
    };
  }, [users]);

  const handleCreditInputChange = (userId, value) => {
    setCreditInputs((prev) => ({ ...prev, [userId]: value }));
  };

  const applyCredits = (user, mode) => {
    const raw = creditInputs[user.id];
    const parsed = parseInt(raw, 10);

    if (Number.isNaN(parsed)) {
      toast.error(
        language === 'he' ? 'אנא הזן מספר קרדיטים תקין' : 'Enter a valid number of credits'
      );
      return;
    }

    const current = Number.isFinite(user.credits) ? user.credits : 0;
    const newCredits = mode === 'set' ? parsed : current + parsed;

    updateUserMutation.mutate({
      userId: user.id,
      updates: { credits: newCredits },
    });
  };

  // Loading: still fetching auth state OR role hasn't been synced from backend yet
  if (isLoadingMe || (currentUser && currentUser.role === undefined)) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-300">
        {language === 'he' ? 'טוען הרשאות...' : 'Loading permissions...'}
      </div>
    );
  }

  if (!currentUser || currentUser.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col gap-2">
          <h1 className="text-3xl font-bold text-white">
            {language === 'he' ? 'פאנל ניהול' : 'Admin Panel'}
          </h1>
          <p className="text-slate-400">
            {language === 'he'
              ? 'נהל משתמשים, קרדיטים וסטטיסטיקות מערכת'
              : 'Manage users, credits and system stats'}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="glass-card border-slate-800">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">
                  {language === 'he' ? 'משתמשים רשומים' : 'Total Users'}
                </p>
                <p className="text-3xl font-bold text-white mt-1">
                  {stats.totalUsers}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500/20 to-purple-500/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-violet-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-slate-800">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">
                  {language === 'he' ? 'סה"כ קרדיטים' : 'Total Credits'}
                </p>
                <p className="text-3xl font-bold text-white mt-1">
                  {stats.totalCredits}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center">
                <Coins className="w-6 h-6 text-amber-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-slate-800">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">
                  {language === 'he' ? 'מנהלים' : 'Admins'}
                </p>
                <p className="text-3xl font-bold text-white mt-1">
                  {stats.admins}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500/20 to-cyan-500/20 flex items-center justify-center">
                <Shield className="w-6 h-6 text-sky-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="glass-card border-slate-800 mb-6">
          <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-white text-xl">
              {language === 'he' ? 'משתמשים' : 'Users'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={handleCreateUser} className="grid gap-4 md:grid-cols-5 bg-slate-900/40 border border-slate-800 rounded-xl p-4">
              <div className="space-y-2 md:col-span-1">
                <Label className="text-slate-300">{language === 'he' ? 'שם' : 'Name'}</Label>
                <Input
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  className="bg-slate-950 border-slate-800 text-white"
                  required
                />
              </div>
              <div className="space-y-2 md:col-span-1">
                <Label className="text-slate-300">Email</Label>
                <Input
                  value={createEmail}
                  onChange={(e) => setCreateEmail(e.target.value)}
                  type="email"
                  className="bg-slate-950 border-slate-800 text-white"
                  required
                />
              </div>
              <div className="space-y-2 md:col-span-1">
                <Label className="text-slate-300">{language === 'he' ? 'סיסמה (ריק = רנדומלי)' : 'Password (empty = random)'}</Label>
                <Input
                  value={createPassword}
                  onChange={(e) => setCreatePassword(e.target.value)}
                  type="text"
                  className="bg-slate-950 border-slate-800 text-white"
                />
              </div>
              <div className="space-y-2 md:col-span-1">
                <Label className="text-slate-300">{language === 'he' ? 'תפקיד' : 'Role'}</Label>
                <select
                  value={createRole}
                  onChange={(e) => setCreateRole(e.target.value)}
                  className="h-10 w-full rounded-md bg-slate-950 border border-slate-800 text-white px-3"
                >
                  <option value="user">user</option>
                  <option value="admin">admin</option>
                </select>
              </div>
              <div className="space-y-2 md:col-span-1">
                <Label className="text-slate-300">{language === 'he' ? 'קרדיטים' : 'Credits'}</Label>
                <div className="flex gap-2">
                  <Input
                    value={createCredits}
                    onChange={(e) => setCreateCredits(e.target.value)}
                    type="number"
                    className="bg-slate-950 border-slate-800 text-white"
                  />
                  <Button
                    type="submit"
                    disabled={createUserMutation.isLoading}
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                  >
                    {language === 'he' ? 'צור' : 'Create'}
                  </Button>
                </div>
              </div>
            </form>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="relative w-full sm:max-w-sm">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  className="pl-9 bg-slate-900/60 border-slate-800 text-slate-100 placeholder:text-slate-500"
                  placeholder={
                    language === 'he'
                      ? 'חפש לפי שם, אימייל או תפקיד'
                      : 'Search by name, email or role'
                  }
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <Button
                variant="ghost"
                className="text-slate-300 hover:text-white hover:bg-slate-800 flex items-center gap-2"
                onClick={() => {
                  queryClient.invalidateQueries(['adminUsers']);
                }}
                disabled={isFetching}
              >
                <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
                {language === 'he' ? 'רענן' : 'Refresh'}
              </Button>
            </div>

            {/* Phones: one card per user. A table can't work here — even after
                moving the credit controls to the 4th column they sat off-screen,
                and horizontal scrolling inside a table is close to undiscoverable
                on touch. The operator tops guests up from a phone at the booth,
                so this is the path that has to be obvious. */}
            <div className="md:hidden space-y-3">
              {isLoading ? (
                <p className="py-6 text-center text-slate-400">
                  {language === 'he' ? 'טוען משתמשים...' : 'Loading users...'}
                </p>
              ) : filteredUsers.length === 0 ? (
                <p className="py-6 text-center text-slate-400">
                  {language === 'he' ? 'לא נמצאו משתמשים' : 'No users found'}
                </p>
              ) : (
                filteredUsers.map((user) => (
                  <div key={user.id} className="rounded-xl border border-slate-800 bg-slate-900/40 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-slate-100 font-medium truncate">{user.name || '—'}</p>
                        <p className="text-xs text-slate-400 truncate">{user.email}</p>
                      </div>
                      <span className="shrink-0 inline-flex items-center rounded-full border border-slate-700 px-2 py-0.5 text-[11px] text-slate-300">
                        {user.role || 'user'}
                      </span>
                    </div>

                    <div className="mt-2 flex items-center gap-3 text-xs text-slate-400">
                      <span>
                        {language === 'he' ? 'קרדיטים' : 'Credits'}:{' '}
                        <b className="text-amber-300 text-sm">
                          {Number.isFinite(user.credits) ? user.credits : 0}
                        </b>
                      </span>
                      <span>
                        {language === 'he' ? 'יצירות' : 'Generations'}:{' '}
                        {Number.isFinite(user.total_generations) ? user.total_generations : 0}
                      </span>
                    </div>

                    <div className="mt-3 flex items-center gap-2">
                      <Input
                        type="number"
                        inputMode="numeric"
                        className="h-10 flex-1 bg-slate-900/60 border-slate-800 text-slate-100"
                        value={creditInputs[user.id] ?? ''}
                        onChange={(e) => handleCreditInputChange(user.id, e.target.value)}
                        placeholder={language === 'he' ? 'כמות קרדיטים' : 'credits amount'}
                      />
                      <Button
                        size="sm"
                        className="h-10 px-3 bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                        disabled={updateUserMutation.isLoading}
                        onClick={() => applyCredits(user, 'add')}
                      >
                        <Plus className="w-4 h-4" />
                        {language === 'he' ? 'הוסף' : 'Add'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-10 px-3 border-slate-700 text-slate-200 shrink-0"
                        disabled={updateUserMutation.isLoading}
                        onClick={() => applyCredits(user, 'set')}
                      >
                        <Equal className="w-4 h-4" />
                        {language === 'he' ? 'קבע' : 'Set'}
                      </Button>
                    </div>

                    <button
                      className="mt-2 text-xs text-violet-400"
                      onClick={() => navigate(`/admin/users/${user.id}`)}
                    >
                      {language === 'he' ? 'צפה בתמונות ←' : 'View images →'}
                    </button>
                  </div>
                ))
              )}
            </div>

            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full text-sm text-left text-slate-300">
                <thead className="bg-slate-900/60 text-slate-400">
                  <tr>
                    <th className="px-4 py-3">{language === 'he' ? 'משתמש' : 'User'}</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">{language === 'he' ? 'תפקיד' : 'Role'}</th>
                    <th className="px-4 py-3">{language === 'he' ? 'קרדיטים' : 'Credits'}</th>
                    <th className="px-4 py-3">{language === 'he' ? 'יצירות' : 'Generations'}</th>
                    <th className="px-4 py-3">{language === 'he' ? 'נוצר' : 'Created'}</th>
                    <th className="px-4 py-3 text-right">
                      {language === 'he' ? 'פעולות' : 'Actions'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-4 py-6 text-center text-slate-400"
                      >
                        {language === 'he' ? 'טוען משתמשים...' : 'Loading users...'}
                      </td>
                    </tr>
                  ) : filteredUsers.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-4 py-6 text-center text-slate-400"
                      >
                        {language === 'he'
                          ? 'לא נמצאו משתמשים'
                          : 'No users found'}
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((user) => (
                      <tr
                        key={user.id}
                        className="border-t border-slate-800/60 hover:bg-slate-900/40"
                      >
                        <td className="px-4 py-3">
                          <div className="flex flex-col">
                            <button
                              type="button"
                              className="font-medium text-white text-left hover:text-violet-300"
                              onClick={() => navigate(`/admin/users/${user.id}`)}
                            >
                              {user.name || '-'}
                            </button>
                            <span className="text-xs text-slate-500">
                              ID: {user.id}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            className="text-slate-300 hover:text-violet-300"
                            onClick={() => navigate(`/admin/users/${user.id}`)}
                          >
                            {user.email}
                          </button>
                        </td>
                        <td className="px-4 py-3 capitalize">
                          <span className="inline-flex items-center rounded-full border border-slate-700 px-2 py-0.5 text-xs text-slate-300">
                            {user.role || 'user'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-amber-300 mb-1">
                            {Number.isFinite(user.credits) ? user.credits : 0}
                          </div>
                          {/* Kept beside the figure on purpose: these used to be
                              the last two columns of a 8-wide table inside an
                              overflow-x container, so on a tablet they sat off
                              the edge of the screen and looked like credits could
                              only be set at sign-up. */}
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              className="h-8 w-20 bg-slate-900/60 border-slate-800 text-slate-100 text-sm"
                              value={creditInputs[user.id] ?? ''}
                              onChange={(e) => handleCreditInputChange(user.id, e.target.value)}
                              placeholder={language === 'he' ? 'כמות' : 'amount'}
                            />
                            <Button
                              size="sm"
                              className="h-8 px-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                              disabled={updateUserMutation.isLoading}
                              onClick={() => applyCredits(user, 'add')}
                              title={language === 'he' ? 'הוסף לקיים' : 'Add to current'}
                            >
                              <Plus className="w-3 h-3" />
                              {language === 'he' ? 'הוסף' : 'Add'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 px-2 border-slate-700 text-slate-200 hover:bg-slate-800"
                              disabled={updateUserMutation.isLoading}
                              onClick={() => applyCredits(user, 'set')}
                              title={language === 'he' ? 'קבע ערך מוחלט' : 'Set exact value'}
                            >
                              <Equal className="w-3 h-3" />
                              {language === 'he' ? 'קבע' : 'Set'}
                            </Button>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-300">
                          {Number.isFinite(user.total_generations) ? user.total_generations : 0}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-400">
                          {formatDate(user.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-slate-700 text-slate-200 hover:bg-slate-800"
                              onClick={() => navigate(`/admin/users/${user.id}`)}
                            >
                              {language === 'he' ? 'תמונות' : 'Images'}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
